// src/app/modules/adminrh/parametres/ParametrageAttestations/ParametrageAttestations.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Plus, X, Trash2, Eye, FileText, Bold, Italic, Underline, Code } from "lucide-react";

import "../parametresStyles/ParametrageAttestations.css";

import {
    fetchAttestationsRequest,
    createAttestationRequest,
    deleteAttestationRequest,
} from "../../../../redux/actions/attestationsActions";

const DYNAMIC_ATTRIBUTES = [
    { key: "[nom]",           label: "Nom",           category: "Identité" },
    { key: "[prenom]",        label: "Prénom",         category: "Identité" },
    { key: "[cin]",           label: "CIN",            category: "Identité" },
    { key: "[matricule]",     label: "Matricule",      category: "Identité" },
    { key: "[poste]",         label: "Poste",          category: "Professionnel" },
    { key: "[departement]",   label: "Département",    category: "Professionnel" },
    { key: "[date_entree]",   label: "Date d'entrée",  category: "Professionnel" },
    { key: "[date_actuelle]", label: "Date actuelle",  category: "Système" },
    { key: "[nom_entreprise]",label: "Nom entreprise", category: "Système" },
];

function groupByCategory(attrs) {
    return attrs.reduce((acc, a) => {
        if (!acc[a.category]) acc[a.category] = [];
        acc[a.category].push(a);
        return acc;
    }, {});
}

export default function ParametrageAttestations() {
    const dispatch = useDispatch();
    const { loading } = useSelector((s) => s.attestations || {});
    const list = useSelector((s) => s.attestations?.attestations || []);

    const [showAddModal, setShowAddModal] = useState(false);
    const [form, setForm] = useState({
        nom: "",
        description: "",
        downloadMode: "FREE",
        corps: "",
        actif: true,
    });

    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewItem, setPreviewItem] = useState(null);

    const editorRef = useRef(null);
    const groupedAttributes = useMemo(() => groupByCategory(DYNAMIC_ATTRIBUTES), []);

    useEffect(() => {
        dispatch(fetchAttestationsRequest());
    }, [dispatch]);

    /* ── helpers ── */
    const resetForm = () => {
        setForm({ nom: "", description: "", downloadMode: "FREE", corps: "", actif: true });
        if (editorRef.current) editorRef.current.innerHTML = "";
    };

    const openCreate  = () => { resetForm(); setShowAddModal(true); };
    const closeCreate = () => { setShowAddModal(false); resetForm(); };

    const exec = (cmd) => {
        try {
            document.execCommand(cmd, false, null);
            if (editorRef.current) setForm((p) => ({ ...p, corps: editorRef.current.innerHTML }));
        } catch (_) {}
    };

    const insertTextAtCursor = (text) => {
        const el = editorRef.current;
        if (!el) return;
        el.focus();
        const sel = window.getSelection?.();
        if (!sel || sel.rangeCount === 0) {
            el.innerHTML += text;
        } else {
            const range = sel.getRangeAt(0);
            range.deleteContents();
            range.insertNode(document.createTextNode(text));
            range.collapse(false);
            sel.removeAllRanges();
            sel.addRange(range);
        }
        setForm((p) => ({ ...p, corps: el.innerHTML }));
    };

    const handleEditorInput = () => {
        setForm((p) => ({ ...p, corps: editorRef.current?.innerHTML || "" }));
    };

    /* ── submit ── */
    const submitCreate = () => {
        if (!form.nom.trim()) {
            Swal.fire({ icon: "warning", title: "Champ requis", text: "Le nom est obligatoire.", confirmButtonColor: "#204385" });
            return;
        }
        if (!form.corps || !String(form.corps).replace(/<[^>]*>/g, "").trim()) {
            Swal.fire({ icon: "warning", title: "Champ requis", text: "Le corps est obligatoire.", confirmButtonColor: "#204385" });
            return;
        }
        dispatch(createAttestationRequest({
            nom: form.nom.trim(),
            description: form.description || "",
            corps: form.corps,
            downloadMode: form.downloadMode,
            actif: true,
        }));
        setShowAddModal(false);
        resetForm();
    };

    const confirmDelete = (id) => {
        Swal.fire({
            title: "Confirmation",
            text: "Voulez-vous vraiment supprimer cette attestation ?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ee645f",
            cancelButtonColor: "#204385",
            confirmButtonText: "Oui, supprimer",
            cancelButtonText: "Annuler",
        }).then((r) => { if (r.isConfirmed) dispatch(deleteAttestationRequest(id)); });
    };

    const openPreview = (item) => { setPreviewItem(item); setPreviewOpen(true); };
    const closePreview = () => { setPreviewOpen(false); setPreviewItem(null); };

    /* ══════════════════════════════════════════
       MODAL — Création
    ══════════════════════════════════════════ */
    const renderModal = () => {
        if (!showAddModal) return null;
        return (
            <div className="pcModal" onMouseDown={closeCreate}>
                <div className="pcModal__panel" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                    <div className="pcModal__header">
                        <div className="pcModal__title">Nouvelle attestation</div>
                        <button className="pcIconBtn" onClick={closeCreate} aria-label="Fermer">
                            <X size={16} />
                        </button>
                    </div>

                    <div className="pcModal__body">
                        <div className="attForm">

                            {/* ligne 1 : nom + description */}
                            <div className="attRow2">
                                <div className="pcField">
                                    <label>Nom *</label>
                                    <input
                                        type="text"
                                        value={form.nom}
                                        onChange={(e) => setForm((p) => ({ ...p, nom: e.target.value }))}
                                        placeholder="Ex : Attestation de Travail"
                                    />
                                </div>
                                <div className="pcField">
                                    <label>Description</label>
                                    <input
                                        type="text"
                                        value={form.description}
                                        onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                                        placeholder="Optionnel"
                                    />
                                </div>
                            </div>

                            {/* ligne 2 : mode */}
                            <div className="attRow2">
                                <div className="pcField">
                                    <label>Mode de téléchargement *</label>
                                    <select
                                        value={form.downloadMode}
                                        onChange={(e) => setForm((p) => ({ ...p, downloadMode: e.target.value }))}
                                    >
                                        <option value="FREE">LIBRE — téléchargement direct</option>
                                        <option value="MOTIF">DEMANDE — motif / validation</option>
                                    </select>
                                </div>
                            </div>

                            {/* éditeur — full width */}
                            <div className="pcField attFieldFull">
                                <label>Corps (éditeur) *</label>

                                <div className="attEditorToolbar">
                                    {/* boutons formatage */}
                                    <div className="attToolbarBtns">
                                        <button type="button" className="attToolbarBtn" title="Gras"     onClick={() => exec("bold")}>
                                            <Bold size={13} />
                                        </button>
                                        <button type="button" className="attToolbarBtn" title="Italique" onClick={() => exec("italic")}>
                                            <Italic size={13} />
                                        </button>
                                        <button type="button" className="attToolbarBtn" title="Souligné" onClick={() => exec("underline")}>
                                            <Underline size={13} />
                                        </button>
                                    </div>

                                    <div className="attEditorSep" />

                                    {/* variables */}
                                    <div className="attVarsHead">
                                        <Code size={13} /> Variables dynamiques
                                    </div>
                                    <div className="attVarsGrid">
                                        {Object.keys(groupedAttributes).map((cat) => (
                                            <div key={cat} className="attVarsCol">
                                                <div className="attVarsCat">{cat}</div>
                                                {groupedAttributes[cat].map((v) => (
                                                    <button
                                                        key={v.key}
                                                        type="button"
                                                        className="attVarBtn"
                                                        onClick={() => insertTextAtCursor(v.key)}
                                                        title={v.key}
                                                    >
                                                        <span>{v.label}</span>
                                                        <code>{v.key}</code>
                                                    </button>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div
                                    ref={editorRef}
                                    className="attEditor"
                                    contentEditable
                                    suppressContentEditableWarning
                                    onInput={handleEditorInput}
                                    data-placeholder="Rédigez le corps ici…"
                                />
                                <div className="attHint">
                                    Astuce : cliquez sur une variable pour l'insérer au curseur (ex.&nbsp;<code>[nom]</code>).
                                </div>
                            </div>

                            {/* checkbox actif */}
                            <label className="attCheck attFieldFull">
                                <input
                                    type="checkbox"
                                    checked={!!form.actif}
                                    onChange={(e) => setForm((p) => ({ ...p, actif: e.target.checked }))}
                                />
                                <div>
                                    <div className="attCheck__title">Actif</div>
                                    <div className="attCheck__sub">Disponible pour les utilisateurs</div>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="pcModal__footer">
                        <button className="pcBtn pcBtn--ghost" type="button" onClick={closeCreate}>
                            Annuler
                        </button>
                        <button className="pcBtn pcBtn--primary" type="button" onClick={submitCreate}>
                            Créer
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    /* ══════════════════════════════════════════
       MODAL — Aperçu
    ══════════════════════════════════════════ */
    const renderPreviewModal = () => {
        if (!previewOpen || !previewItem) return null;
        return (
            <div className="pcModal" onMouseDown={closePreview}>
                <div className="pcModal__panel pcModal__panel--sm" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                    <div className="pcModal__header">
                        <div className="pcModal__title">
                            <Eye size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />
                            Aperçu — {previewItem.nom}
                        </div>
                        <button className="pcIconBtn" onClick={closePreview} aria-label="Fermer">
                            <X size={16} />
                        </button>
                    </div>

                    <div className="pcModal__body">
                        <div className="attPreviewBox">
                            <div
                                className="attPreviewContent"
                                dangerouslySetInnerHTML={{ __html: previewItem.corps || "" }}
                            />
                        </div>
                    </div>

                    <div className="pcModal__footer">
                        <button className="pcBtn pcBtn--ghost" type="button" onClick={closePreview}>
                            Fermer
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    /* ══════════════════════════════════════════
       RENDER
    ══════════════════════════════════════════ */
    return (
        <div className="pcPage">
            {/* Header */}
            <div className="pcHeader">
                <div>
                    <h1>Paramétrage des attestations</h1>
                    <p>Configurez les types d'attestations et leurs paramètres.</p>
                </div>
            </div>

            {/* Contenu */}
            <div className="pcWrap">
                <div className="pcCard">
                    <div className="pcBody">
                        <div className="pcTopRow">
                            <h2 className="pcTitle">Liste des attestations</h2>
                            <button
                                className="pcBtn pcBtn--primary"
                                type="button"
                                onClick={openCreate}
                                disabled={loading}
                            >
                                <Plus size={14} /> Ajouter
                            </button>
                        </div>

                        <div className="pcTableWrap">
                            <table className="pcTable">
                                <thead>
                                <tr>
                                    <th>Nom</th>
                                    <th>Description</th>
                                    <th>Mode</th>
                                    <th>Statut</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={5} className="pcEmptyCell">Chargement…</td>
                                    </tr>
                                ) : list.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="pcEmptyCell">Aucune attestation configurée</td>
                                    </tr>
                                ) : (
                                    list.map((a) => (
                                        <tr key={a.id}>
                                            <td>
                                                    <span className="pcInline">
                                                        <FileText size={14} className="pcAccent" />
                                                        <span className="pcStrong">{a.nom}</span>
                                                    </span>
                                            </td>
                                            <td className="pcMuted">{a.description || "—"}</td>
                                            <td>
                                                <span className="pcBadge">{a.downloadMode}</span>
                                            </td>
                                            <td>
                                                <button
                                                    className={`pcPill ${a.actif ? "is-on" : "is-off"}`}
                                                    type="button"
                                                    disabled
                                                >
                                                    {a.actif ? "Actif" : "Inactif"}
                                                </button>
                                            </td>
                                            <td className="is-right">
                                                <div className="pcRowActions">
                                                    <button
                                                        className="pcIconBtn"
                                                        type="button"
                                                        title="Aperçu"
                                                        onClick={() => openPreview(a)}
                                                        disabled={loading}
                                                    >
                                                        <Eye size={15} />
                                                    </button>
                                                    <button
                                                        className="pcIconBtn pcIconBtn--danger"
                                                        type="button"
                                                        title="Supprimer"
                                                        onClick={() => confirmDelete(a.id)}
                                                        disabled={loading}
                                                    >
                                                        <Trash2 size={15} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {renderModal()}
            {renderPreviewModal()}
        </div>
    );
}
